/**
 * Trivia Management Tab
 *
 * Admin interface for managing daily trivia questions.
 */

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  HelpCircle,
  Plus,
  Edit,
  Trash2,
  X,
  Upload,
  Loader2,
  Calendar,
  Check,
  Award,
  BarChart3,
  Eye,
  EyeOff,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { TriviaQuestion, TriviaOption, TriviaDifficulty } from "../../types";
import { dbGet, dbSet, dbRemove, dbUpdate, subscribeToPath, uploadFile } from "../../config/firebase";
import { useTestMode } from "../../contexts/TestModeContext";
import { useAuth } from "../../contexts/AuthContext";

const DIFFICULTY_CONFIG: Record<TriviaDifficulty, { label: string; color: string; bgColor: string }> = {
  easy: { label: "Easy", color: "text-green-600", bgColor: "bg-green-100 dark:bg-green-900/30" },
  medium: { label: "Medium", color: "text-yellow-600", bgColor: "bg-yellow-100 dark:bg-yellow-900/30" },
  hard: { label: "Hard", color: "text-red-600", bgColor: "bg-red-100 dark:bg-red-900/30" },
};

const SPORTS_OPTIONS = [
  "Football", "Basketball", "Baseball", "Hockey", "Soccer",
  "MMA", "Boxing", "Golf", "Tennis", "General Sports"
];

const LEAGUE_OPTIONS: Record<string, string[]> = {
  "Football": ["NFL", "NCAAF", "CFL", "XFL"],
  "Basketball": ["NBA", "NCAAB", "WNBA", "EuroLeague"],
  "Baseball": ["MLB", "NPB", "KBO"],
  "Hockey": ["NHL", "AHL", "KHL"],
  "Soccer": ["EPL", "La Liga", "Serie A", "Bundesliga", "MLS", "Champions League"],
  "MMA": ["UFC", "Bellator", "PFL", "ONE"],
  "Boxing": ["Boxing"],
  "Golf": ["PGA", "LIV", "LPGA"],
  "Tennis": ["ATP", "WTA", "Grand Slam"],
  "General Sports": ["General"],
};

interface TriviaFormData {
  date: string;
  question: string;
  questionImageUrl: string;
  options: TriviaOption[];
  correctIndices: number[];
  sport: string;
  league: string;
  difficulty: TriviaDifficulty;
  gtReward: number;
  streakBonusPerDay: number;
  isActive: boolean;
}

const getDefaultFormData = (question: TriviaQuestion | null): TriviaFormData => {
  const today = new Date().toISOString().split('T')[0];
  return {
    date: question?.date || today,
    question: question?.question || "",
    questionImageUrl: question?.questionImageUrl || "",
    options: question?.options || [
      { text: "", imageUrl: "" },
      { text: "", imageUrl: "" },
      { text: "", imageUrl: "" },
      { text: "", imageUrl: "" },
    ],
    correctIndices: question?.correctIndices || [],
    sport: question?.sport || "",
    league: question?.league || "",
    difficulty: question?.difficulty || "medium",
    gtReward: question?.gtReward || 5,
    streakBonusPerDay: question?.streakBonusPerDay || 1,
    isActive: question?.isActive ?? true,
  };
};

interface TriviaModalProps {
  question: TriviaQuestion | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: TriviaFormData) => Promise<void>;
}

const TriviaModal: React.FC<TriviaModalProps> = ({ question, isOpen, onClose, onSave }) => {
  const isEditing = !!question;
  const [formData, setFormData] = useState<TriviaFormData>(getDefaultFormData(question));
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setFormData(getDefaultFormData(question));
  }, [question, isOpen]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: "question" | number) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const timestamp = Date.now();
      const path = `trivia/images/${timestamp}_${file.name}`;
      const downloadURL = await uploadFile(path, file);

      if (type === "question") {
        setFormData((prev) => ({ ...prev, questionImageUrl: downloadURL }));
      } else {
        setFormData((prev) => {
          const newOptions = [...prev.options];
          newOptions[type] = { ...newOptions[type], imageUrl: downloadURL };
          return { ...prev, options: newOptions };
        });
      }
    } catch (error) {
      console.error("Image upload failed:", error);
      alert("Failed to upload image");
    } finally {
      setIsUploading(false);
    }
  };

  const toggleCorrectAnswer = (index: number) => {
    setFormData((prev) => {
      const newIndices = prev.correctIndices.includes(index)
        ? prev.correctIndices.filter((i) => i !== index)
        : [...prev.correctIndices, index];
      return { ...prev, correctIndices: newIndices };
    });
  };

  const updateOption = (index: number, text: string) => {
    setFormData((prev) => {
      const newOptions = [...prev.options];
      newOptions[index] = { ...newOptions[index], text };
      return { ...prev, options: newOptions };
    });
  };

  const handleSubmit = async () => {
    if (!formData.question.trim()) {
      alert("Please enter a question");
      return;
    }
    if (formData.options.filter((o) => o.text.trim()).length < 2) {
      alert("Please enter at least 2 options");
      return;
    }
    if (formData.correctIndices.length === 0) {
      alert("Please select at least one correct answer");
      return;
    }

    setIsSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error("Save failed:", error);
      alert("Failed to save question");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b dark:border-gray-800">
            <h2 className="text-lg font-bold">
              {isEditing ? "Edit Trivia Question" : "Create Trivia Question"}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
              <X size={20} />
            </button>
          </div>

          {/* Form */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Date */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Date *
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
              />
              <p className="text-xs text-gray-500">One trivia question per day</p>
            </div>

            {/* Question */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Question *
              </label>
              <textarea
                value={formData.question}
                onChange={(e) => setFormData((prev) => ({ ...prev, question: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none resize-none"
                rows={3}
                placeholder="Enter your trivia question"
              />
            </div>

            {/* Question Image */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Question Image (Optional)
              </label>
              <div className="flex items-center gap-4">
                {formData.questionImageUrl ? (
                  <img src={formData.questionImageUrl} alt="Question" className="w-20 h-20 object-cover rounded-lg" />
                ) : (
                  <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                    <ImageIcon size={24} className="text-gray-400" />
                  </div>
                )}
                <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-sm font-medium cursor-pointer transition-colors">
                  <Upload size={16} />
                  Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, "question")}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            {/* Options */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Options * (Click checkmark to mark correct)
              </label>
              <div className="space-y-2">
                {formData.options.map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <button
                      onClick={() => toggleCorrectAnswer(index)}
                      className={`p-2 rounded-lg transition-colors ${
                        formData.correctIndices.includes(index)
                          ? "bg-green-100 dark:bg-green-900/30 text-green-600"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-400 hover:bg-gray-200"
                      }`}
                      title={formData.correctIndices.includes(index) ? "Correct answer" : "Mark as correct"}
                    >
                      <Check size={16} />
                    </button>
                    <input
                      type="text"
                      value={option.text}
                      onChange={(e) => updateOption(index, e.target.value)}
                      className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                      placeholder={`Option ${index + 1}`}
                    />
                    <span className="text-sm text-gray-400 w-8">{String.fromCharCode(65 + index)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sport & League */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Sport</label>
                <select
                  value={formData.sport}
                  onChange={(e) => setFormData((prev) => ({ ...prev, sport: e.target.value, league: "" }))}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                >
                  <option value="">Select sport</option>
                  {SPORTS_OPTIONS.map((sport) => (
                    <option key={sport} value={sport}>{sport}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">League</label>
                <select
                  value={formData.league}
                  onChange={(e) => setFormData((prev) => ({ ...prev, league: e.target.value }))}
                  disabled={!formData.sport}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50"
                >
                  <option value="">Select league</option>
                  {formData.sport && LEAGUE_OPTIONS[formData.sport]?.map((league) => (
                    <option key={league} value={league}>{league}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Difficulty & Rewards */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Difficulty</label>
                <select
                  value={formData.difficulty}
                  onChange={(e) => setFormData((prev) => ({ ...prev, difficulty: e.target.value as TriviaDifficulty }))}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">GT Reward</label>
                <input
                  type="number"
                  min="1"
                  value={formData.gtReward}
                  onChange={(e) => setFormData((prev) => ({ ...prev, gtReward: parseInt(e.target.value) || 5 }))}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Streak Bonus</label>
                <input
                  type="number"
                  min="0"
                  value={formData.streakBonusPerDay}
                  onChange={(e) => setFormData((prev) => ({ ...prev, streakBonusPerDay: parseInt(e.target.value) || 1 }))}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                />
              </div>
            </div>

            {/* Active Status */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setFormData((prev) => ({ ...prev, isActive: !prev.isActive }))}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  formData.isActive
                    ? "bg-green-100 dark:bg-green-900/30 text-green-600"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-500"
                }`}
              >
                {formData.isActive ? <Eye size={16} /> : <EyeOff size={16} />}
                {formData.isActive ? "Active" : "Inactive"}
              </button>
              <span className="text-sm text-gray-500">
                {formData.isActive ? "Question will be shown to users" : "Question is hidden"}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 p-4 border-t dark:border-gray-800">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSaving || isUploading}
              className="flex-1 px-4 py-2.5 bg-primary hover:bg-primary-dark text-black font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>Save Question</>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Question Row Component
const TriviaQuestionRow: React.FC<{
  question: TriviaQuestion;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
}> = ({ question, onEdit, onDelete, onToggleActive }) => {
  const difficulty = DIFFICULTY_CONFIG[question.difficulty];
  const totalAnswered = question.totalAnswers || 0;
  const correctRate = totalAnswered > 0 ? Math.round((question.correctAnswers / totalAnswered) * 100) : 0;

  const isPast = new Date(question.date) < new Date(new Date().toDateString());
  const isToday = question.date === new Date().toISOString().split('T')[0];

  return (
    <div className={`p-4 rounded-lg border transition-colors ${
      isToday
        ? "bg-primary/5 border-primary dark:border-primary"
        : "bg-surface-light dark:bg-surface-dark border-gray-200 dark:border-gray-700"
    }`}>
      <div className="flex items-start gap-4">
        {/* Date */}
        <div className={`text-center px-3 py-2 rounded-lg ${
          isToday ? "bg-primary text-black" : "bg-gray-100 dark:bg-gray-800"
        }`}>
          <p className="text-xs font-medium">{new Date(question.date).toLocaleDateString("en-US", { month: "short" })}</p>
          <p className="text-xl font-bold">{new Date(question.date).getDate()}</p>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${difficulty.bgColor} ${difficulty.color}`}>
              {difficulty.label}
            </span>
            {question.sport && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600">
                {question.sport}
              </span>
            )}
            {!question.isActive && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                Inactive
              </span>
            )}
            {isToday && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary/20 text-primary font-bold">
                TODAY
              </span>
            )}
          </div>
          <p className="font-medium line-clamp-2">{question.question}</p>
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Award size={14} className="text-primary" />
              {question.gtReward} GT
            </span>
            {totalAnswered > 0 && (
              <>
                <span className="flex items-center gap-1">
                  <BarChart3 size={14} />
                  {totalAnswered} answers
                </span>
                <span className="flex items-center gap-1">
                  <Check size={14} className="text-green-500" />
                  {correctRate}% correct
                </span>
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={onToggleActive}
            className={`p-2 rounded-lg transition-colors ${
              question.isActive
                ? "hover:bg-yellow-100 dark:hover:bg-yellow-900/30 text-yellow-600"
                : "hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600"
            }`}
            title={question.isActive ? "Deactivate" : "Activate"}
          >
            {question.isActive ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
          <button
            onClick={onEdit}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit size={18} />
          </button>
          {!isPast && (
            <button
              onClick={onDelete}
              className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 rounded-lg transition-colors"
              title="Delete"
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Tab Component
export const TriviaManagementTab: React.FC = () => {
  const { user } = useAuth();
  const { getPath } = useTestMode();
  const [questions, setQuestions] = useState<TriviaQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<TriviaQuestion | null>(null);
  const [viewMonth, setViewMonth] = useState(new Date());

  // Subscribe to trivia questions
  useEffect(() => {
    const path = getPath("trivia/questions");
    const unsubscribe = subscribeToPath(
      path,
      (data) => {
        if (data) {
          const questionList = Object.values(data) as TriviaQuestion[];
          setQuestions(questionList.sort((a, b) => b.date.localeCompare(a.date)));
        } else {
          setQuestions([]);
        }
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching trivia questions:", error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [getPath]);

  // Filter questions by month
  const monthStart = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  const monthEnd = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0);

  const filteredQuestions = questions.filter((q) => {
    const date = new Date(q.date);
    return date >= monthStart && date <= monthEnd;
  });

  const handleSave = async (data: TriviaFormData) => {
    const path = getPath(`trivia/questions/${data.date}`);

    // Build the question data object, excluding undefined/empty optional fields
    // Firebase doesn't allow undefined values
    const questionData: Record<string, any> = {
      questionId: data.date,
      date: data.date,
      question: data.question,
      options: data.options.filter((o) => o.text.trim()).map(o => ({
        text: o.text,
        ...(o.imageUrl ? { imageUrl: o.imageUrl } : {})
      })),
      correctIndices: data.correctIndices,
      difficulty: data.difficulty,
      gtReward: data.gtReward,
      streakBonusPerDay: data.streakBonusPerDay,
      totalAnswers: editingQuestion?.totalAnswers || 0,
      correctAnswers: editingQuestion?.correctAnswers || 0,
      optionStats: editingQuestion?.optionStats || data.options.filter(o => o.text.trim()).map(() => 0),
      isActive: data.isActive,
      createdAt: editingQuestion?.createdAt || Date.now(),
      createdBy: editingQuestion?.createdBy || user?.uid || "admin",
    };

    // Only add optional fields if they have values
    if (data.questionImageUrl) {
      questionData.questionImageUrl = data.questionImageUrl;
    }
    if (data.sport) {
      questionData.sport = data.sport;
    }
    if (data.league) {
      questionData.league = data.league;
    }

    await dbSet(path, questionData);
  };

  const handleDelete = async (date: string) => {
    if (!window.confirm("Are you sure you want to delete this question?")) return;

    try {
      await dbRemove(getPath(`trivia/questions/${date}`));
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete question");
    }
  };

  const handleToggleActive = async (question: TriviaQuestion) => {
    await dbUpdate(getPath(`trivia/questions/${question.date}`), {
      isActive: !question.isActive,
    });
  };

  const handleEdit = (question: TriviaQuestion) => {
    setEditingQuestion(question);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingQuestion(null);
  };

  const changeMonth = (delta: number) => {
    setViewMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };

  // Stats
  const stats = {
    total: questions.length,
    thisMonth: filteredQuestions.length,
    active: questions.filter((q) => q.isActive).length,
    totalAnswers: questions.reduce((sum, q) => sum + (q.totalAnswers || 0), 0),
  };

  const today = new Date().toISOString().split('T')[0];
  const hasQuestionForToday = questions.some((q) => q.date === today);

  return (
    <motion.div
      key="trivia"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-4"
    >
      {/* Stats */}
      <div className="card p-5">
        <h3 className="text-lg font-bold mb-4">Trivia Statistics</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <HelpCircle size={16} className="text-primary" />
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Total Questions</p>
            </div>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar size={16} className="text-blue-500" />
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">This Month</p>
            </div>
            <p className="text-2xl font-bold text-blue-500">{stats.thisMonth}</p>
          </div>
          <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye size={16} className="text-green-500" />
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Active</p>
            </div>
            <p className="text-2xl font-bold text-green-500">{stats.active}</p>
          </div>
          <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 size={16} className="text-purple-500" />
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Total Responses</p>
            </div>
            <p className="text-2xl font-bold text-purple-500">{stats.totalAnswers.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Today's Status */}
      {!hasQuestionForToday && (
        <div className="card p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <Calendar size={20} className="text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-amber-800 dark:text-amber-200">No trivia for today!</p>
              <p className="text-sm text-amber-600 dark:text-amber-400">Create a question for {new Date().toLocaleDateString()}</p>
            </div>
            <button
              onClick={() => {
                setEditingQuestion(null);
                setShowModal(true);
              }}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors"
            >
              Create Now
            </button>
          </div>
        </div>
      )}

      {/* Questions List */}
      <div className="card p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-bold">Questions</h3>
            {/* Month Navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => changeMonth(-1)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="font-medium min-w-[120px] text-center">
                {viewMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
              </span>
              <button
                onClick={() => changeMonth(1)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
          <button
            onClick={() => {
              setEditingQuestion(null);
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark text-black font-medium rounded-lg transition-colors"
          >
            <Plus size={18} />
            Add Question
          </button>
        </div>

        {/* Questions List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-primary" />
          </div>
        ) : filteredQuestions.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <HelpCircle size={48} className="mx-auto mb-4 opacity-20" />
            <p>No trivia questions for this month</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredQuestions.map((question) => (
              <TriviaQuestionRow
                key={question.questionId}
                question={question}
                onEdit={() => handleEdit(question)}
                onDelete={() => handleDelete(question.date)}
                onToggleActive={() => handleToggleActive(question)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      <TriviaModal
        question={editingQuestion}
        isOpen={showModal}
        onClose={handleCloseModal}
        onSave={handleSave}
      />
    </motion.div>
  );
};

export default TriviaManagementTab;
